import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import DeleteDocumentAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/DocumentActions/DeleteDocumentAction';
import DocumentSourcesSummary from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentVerificationHeader/DocumentSourcesSummary';
import DownloadDocumentAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/DocumentActions/DownloadDocumentAction';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import type { DocumentListReturn } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentListReturn';
import type { ReviewStage } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useReviewStage';
import editingFieldSignal from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import { setPendingDiscard } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/pendingDiscardSignal';
import DocumentStatusTag from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/DocumentStatusTag';
import BackLink from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/BackLink';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import ValidateDocumentAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/DocumentActions/ValidateDocumentAction';
import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import useFacilityUsers from '@UI/Pages/Beneficiaries/Hooks/useFacilityUsers';
import { Message } from '@verclam/ui-kit';

const headerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const titleRowStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	flex-wrap: wrap;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex-direction: column;
		align-items: stretch;
	}
`;

const titleGroupStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const actionsStyle = css`
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	flex-wrap: wrap;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex-direction: column;
		align-items: stretch;
	}
`;

const titleStyle = css`
	${theme.typography.heroTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly dossierName: string;
	/** The stage the screen was opened for, `null` when it was opened on a single document. */
	readonly reviewStage: ReviewStage | null;
	/** The way back to the documents list, `null` unless opened from it; a review stage takes precedence. */
	readonly listReturn: DocumentListReturn | null;
};

/**
 * The screen's own header: the breadcrumb and, opened from a preparation, the « ← {preparation} »
 * link — both guarded (a modified draft queues a discard request through `pendingDiscardSignal`
 * instead of navigating away silently) — the eyebrow, the title (« Relire et corriger » for whoever
 * can write, « Relecture » otherwise) with the document's own status, the « Document vérifié » banner,
 * the read-only banner, and the actions group (Valider, Télécharger, Supprimer), each deciding its own
 * visibility. On a phone the header keeps the title and the status only: Télécharger and Supprimer
 * live in `MobileActionBar`. Reads `state.documentsStore.document` itself rather than
 * taking it as a prop.
 */
const DocumentVerificationHeader: React.FC<Props> = ({ subject, dossierName, reviewStage, listReturn }) => {
	const document = useSelector((state) => state.documentsStore.document);
	const { data: facilityUsers } = useFacilityUsers();

	const canEdit = useCanEditDocument();
	const isCompact = useIsCompactViewport();

	const handleBackClick = React.useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			const editingField = editingFieldSignal.peek();

			if (editingField?.isDirty) {
				event.preventDefault();
				setPendingDiscard({ type: 'back', path: buildSubjectPath(subject) });
			}
		},
		[subject],
	);

	const preparationPath = reviewStage?.preparationPath ?? null;

	const handlePreparationLinkClick = React.useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			const editingField = editingFieldSignal.peek();

			if (editingField?.isDirty && preparationPath !== null) {
				event.preventDefault();
				setPendingDiscard({ type: 'back', path: preparationPath });
			}
		},
		[preparationPath],
	);

	const listPath = reviewStage === null ? (listReturn?.listPath ?? null) : null;

	const handleListLinkClick = React.useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			const editingField = editingFieldSignal.peek();

			if (editingField?.isDirty && listPath !== null) {
				event.preventDefault();
				setPendingDiscard({ type: 'back', path: listPath });
			}
		},
		[listPath],
	);

	const preparationLabel = reviewStage?.preparation?.label ?? 'Préparation';

	const breadcrumbItems = React.useMemo(() => {
		if (document === null) {
			return [];
		}

		if (preparationPath !== null) {
			return [{ label: preparationLabel, url: preparationPath }, { label: document.title }];
		}

		return listPath !== null
			? [{ label: 'Documents', url: listPath }, { label: document.title }]
			: [{ label: document.title }];
	}, [document, preparationPath, preparationLabel, listPath]);

	const validatedLine = React.useMemo(() => {
		if (document === null || document.validatedAt === null) {
			return null;
		}

		const validator = facilityUsers?.findById(document.validatedById) ?? null;
		const datePart = formatLongDate(document.validatedAt);

		return validator !== null
			? `Document vérifié le ${datePart} par ${validator.fullName}`
			: `Document vérifié le ${datePart}`;
	}, [document, facilityUsers]);

	if (document === null) {
		return null;
	}

	return (
		<header css={headerStyle}>
			<SubjectBreadcrumb
				subject={subject}
				items={breadcrumbItems}
				onDossierLinkClick={handleBackClick}
			/>
			<ConditionalLoader data={reviewStage}>
				{(stage) => (
					<BackLink
						to={stage.preparationPath}
						label={preparationLabel}
						onClick={handlePreparationLinkClick}
					/>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={listPath}>
				{(path) => <BackLink to={path} label="Documents" onClick={handleListLinkClick} />}
			</ConditionalLoader>
			<ConditionalLoader data={!isCompact}>
				{() => (
					<span css={eyebrowStyle}>
						{(reviewStage !== null
							? `${dossierName} · étape ${reviewStage.stage + 1} · ${document.title}`
							: `${dossierName} · ${document.title}`
						).toUpperCase()}
					</span>
				)}
			</ConditionalLoader>
			<div css={titleRowStyle}>
				<div css={titleGroupStyle}>
					<h1 css={titleStyle}>{canEdit ? 'Relire et corriger' : 'Relecture'}</h1>
					<DocumentStatusTag status={document.status} />
				</div>
				<ConditionalLoader data={!isCompact}>
					{() => (
						<div css={actionsStyle}>
							<ValidateDocumentAction
								subject={subject}
								reviewStage={reviewStage}
								listReturn={listReturn}
							/>
							<DownloadDocumentAction />
							<DeleteDocumentAction />
						</div>
					)}
				</ConditionalLoader>
			</div>
			<ConditionalLoader data={!isCompact}>{() => <DocumentSourcesSummary />}</ConditionalLoader>
			<ConditionalLoader data={validatedLine}>
				{(line) => <Message severity="success" role="status" text={line} />}
			</ConditionalLoader>
			<ConditionalLoader data={!canEdit}>
				{() => (
					<Message severity="secondary" role="status" text="Ce document est en lecture seule." />
				)}
			</ConditionalLoader>
		</header>
	);
};

export default DocumentVerificationHeader;
